import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  createDefinition,
  getDefinition,
  updateDefinition,
  type CreateDefinitionInput,
} from '../../api/workflowApi.js';
import { listProcessBindings, type ProcessBindingRow } from '../../api/wfBindingsApi.js';
import { FormShell, Field, SelectField, CheckboxField, SaveBar } from './wfKit.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

/** Sentinel for definitions that serve no catalogued process, so the raw fields stay reachable. */
const MANUAL = '__MANUAL__';

/**
 * WorkflowDefinitionFormPage — create / edit a workflow definition master (T045).
 *
 * Fixes UI Audit M04 C1 (the designer stub had no persistence): this screen
 * persists a real definition via POST/PATCH `/v1/wf/definitions`. Editing is
 * only meaningful while the definition is DRAFT (the backend rejects edits to
 * ACTIVE/DEPRECATED definitions); the designer home drives users here only for
 * DRAFT rows.
 */
export function WorkflowDefinitionFormPage(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);

  const [workflowCode, setWorkflowCode] = useState('');
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [processCode, setProcessCode] = useState('');
  const [entityType, setEntityType] = useState('');
  const [moduleCode, setModuleCode] = useState('');
  const [isParallelAllowed, setIsParallelAllowed] = useState(false);
  const [maxApprovalLevels, setMaxApprovalLevels] = useState('1');
  const [autoCancellationHours, setAutoCancellationHours] = useState('');
  const [allowsRecall, setAllowsRecall] = useState(false);
  const [recallWindowHours, setRecallWindowHours] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState('');
  const [effectiveTo, setEffectiveTo] = useState('');

  const [processes, setProcesses] = useState<readonly ProcessBindingRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(editing);

  useEffect(() => {
    let alive = true;
    listProcessBindings(token)
      .then((r) => { if (alive) setProcesses(r.processes); })
      .catch((e) => {
        // The failure used to be swallowed and the list simply emptied, so the
        // picker silently collapsed to "Other — enter entity and module
        // manually" and the administrator had no way to know the catalogue had
        // not loaded. Typing the entity by hand is how a workflow ends up
        // describing something no process actually raises — the same shape of
        // mistake that left RKH's budget binding pointing at a workflow that
        // did not exist. Say so instead.
        if (!alive) return;
        setProcesses([]);
        setError((e as Error).message || t('wf.defForm.purposeLoadFailed'));
      });
    return () => { alive = false; };
  }, [token, t]);

  const processOptions = useMemo(() => {
    const rows = processes.map((p) => ({
      value: p.processCode,
      label: `${t(p.labelKey)} — ${p.moduleCode} ${p.moduleLabelKey ? t(p.moduleLabelKey) : ''}`.trim(),
    }));
    rows.sort((a, b) => a.label.localeCompare(b.label));
    return [...rows, { value: MANUAL, label: t('wf.defForm.purposeManual') }];
  }, [processes, t]);

  /** An existing definition predates the picker, so fall back to the raw fields. */
  useEffect(() => {
    if (!editing || processCode || !entityType || processes.length === 0) return;
    const hit = processes.find((p) => p.entityType === entityType);
    setProcessCode(hit ? hit.processCode : MANUAL);
  }, [editing, processCode, entityType, processes]);

  function pickProcess(code: string): void {
    setProcessCode(code);
    if (code === MANUAL || code === '') return;
    const p = processes.find((x) => x.processCode === code);
    if (!p) return;
    setEntityType(p.entityType);
    setModuleCode(p.moduleCode);
  }

  useEffect(() => {
    if (!id) return;
    let alive = true;
    getDefinition(token, id)
      .then((d) => {
        if (!alive) return;
        setWorkflowCode(d.workflowCode);
        setWorkflowName(d.workflowName);
        setWorkflowDescription(d.workflowDescription ?? '');
        setEntityType(d.entityType);
        setModuleCode(d.moduleCode);
        setIsParallelAllowed(Boolean(d.isParallelAllowed));
        setMaxApprovalLevels(String(d.maxApprovalLevels ?? 1));
        setAutoCancellationHours(d.autoCancellationHours != null ? String(d.autoCancellationHours) : '');
        setAllowsRecall(Boolean(d.allowsRecall));
        setRecallWindowHours(d.recallWindowHours != null ? String(d.recallWindowHours) : '');
        setEffectiveFrom(d.effectiveFrom ? String(d.effectiveFrom).slice(0, 10) : '');
        setEffectiveTo(d.effectiveTo ? String(d.effectiveTo).slice(0, 10) : '');
        setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, id]);

  async function submit(): Promise<void> {
    if (!workflowName.trim()) { setError(t('wf.defForm.nameReq')); return; }
    if (!editing && !workflowCode.trim()) { setError(t('wf.defForm.codeReq')); return; }
    if (!processCode) { setError(t('wf.defForm.purposeReq')); return; }
    if (!entityType.trim()) { setError(t('wf.defForm.entityReq')); return; }
    if (!editing && !moduleCode.trim()) { setError(t('wf.defForm.moduleReq')); return; }
    setError(null);
    setBusy(true);
    try {
      const common = {
        workflowName: workflowName.trim(),
        workflowDescription: workflowDescription.trim() || undefined,
        entityType: entityType.trim(),
        isParallelAllowed,
        maxApprovalLevels: Number(maxApprovalLevels) || 1,
        autoCancellationHours: autoCancellationHours ? Number(autoCancellationHours) : undefined,
        allowsRecall,
        recallWindowHours: recallWindowHours ? Number(recallWindowHours) : undefined,
        effectiveFrom: effectiveFrom || undefined,
        effectiveTo: effectiveTo || undefined,
      };
      if (editing && id) {
        await updateDefinition(token, id, common);
        navigate(`/admin/wf/definitions/${id}/design`);
      } else {
        const body: CreateDefinitionInput = {
          ...common,
          workflowCode: workflowCode.trim(),
          moduleCode: moduleCode.trim(),
        };
        const created = await createDefinition(token, body);
        navigate(`/admin/wf/definitions/${created.workflowDefinitionId}/design`);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return (
      <main aria-label="wf-definition-form">
        <p>{t('common.loading')}</p>
      </main>
    );
  }

  return (
    <FormShell
      label="wf-definition-form"
      title={editing ? t('wf.defForm.editTitle') : t('wf.defForm.newTitle')}
      error={error}
      onSubmit={submit}
    >
      <Field label={t('wf.defForm.codeLabel')} name="workflowCode" value={workflowCode} onChange={setWorkflowCode} required placeholder={t('wf.defForm.codePh')} />
      <Field label={t('wf.defForm.nameLabel')} name="workflowName" value={workflowName} onChange={setWorkflowName} required />
      <Field label={t('wf.defForm.descLabel')} name="workflowDescription" value={workflowDescription} onChange={setWorkflowDescription} />
      <SelectField
        label={t('wf.defForm.purposeLabel')}
        name="processCode"
        value={processCode}
        onChange={pickProcess}
        options={processOptions}
        required
        placeholder={t('wf.defForm.purposePh')}
      />
      {processCode === MANUAL && (
        <>
          <Field label={t('wf.defForm.entityTypeLabel')} name="entityType" value={entityType} onChange={setEntityType} required placeholder={t('wf.defForm.entityPh')} />
          <Field label={t('wf.defForm.moduleLabel')} name="moduleCode" value={moduleCode} onChange={setModuleCode} required placeholder={t('wf.defForm.modulePh')} />
        </>
      )}
      <Field label={t('wf.defForm.maxLevelsLabel')} name="maxApprovalLevels" value={maxApprovalLevels} onChange={setMaxApprovalLevels} type="number" />
      <CheckboxField label={t('wf.defForm.parallelLabel')} name="isParallelAllowed" checked={isParallelAllowed} onChange={setIsParallelAllowed} />
      <Field label={t('wf.defForm.autoCancelLabel')} name="autoCancellationHours" value={autoCancellationHours} onChange={setAutoCancellationHours} type="number" />
      <CheckboxField label={t('wf.defForm.recallLabel')} name="allowsRecall" checked={allowsRecall} onChange={setAllowsRecall} />
      <Field label={t('wf.defForm.recallWindowLabel')} name="recallWindowHours" value={recallWindowHours} onChange={setRecallWindowHours} type="number" />
      <Field label={t('wf.common.effectiveFrom')} name="effectiveFrom" value={effectiveFrom} onChange={setEffectiveFrom} type="date" />
      <Field label={t('wf.common.effectiveTo')} name="effectiveTo" value={effectiveTo} onChange={setEffectiveTo} type="date" />
      <SaveBar busy={busy} saveLabel={editing ? t('wf.defForm.saveChanges') : t('wf.defForm.createDef')} cancelTo="/admin/workflows" />
    </FormShell>
  );
}
